import * as React from "react";

import { makeStyles, createStyles, Paper } from "@material-ui/core";
import {
    DisplayField,
    formatTelUri,
    Label,
    Link,
    Page,
    PageSection,
    RelodeTheme,
    Text,
} from "../../../components";
import RelodeLogoHeader from "../../../assets/images/relode_logo_header.png";
import RelodeLogoFooter from "../../../assets/images/relode_icon_footer.png";
import FacebookLogo from "../../../assets/images/facebook.png";
import InstagramLogo from "../../../assets/images/instagram.png";
import TwitterLogo from "../../../assets/images/twitter.png";
import { Stakeholder, TimeZone } from "../../../domain";
import { formatPhoneText, getUserFromStorage } from "../../../modules";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                flexBasis: "50%",
                fontSize: "16px",
            },
            emailContent: {
                padding: theme.spacing(4),
            },
            header: {
                margin: `${theme.spacing(4)}px 0`,
                display: "flex",
                justifyContent: "center",
            },
            paper: {
                maxWidth: 720,
                display: "flex",
                flexDirection: "column",
                padding: theme.spacing(5, 3),
            },
            emailBodyContainer: {
                display: "flex",
                justifyContent: "center",
            },
            address: {
                margin: theme.spacing(4, 0, 0, 0),
            },
            emailBodyText: {
                margin: theme.spacing(2, 0),
            },
            emailBodyCard: {
                backgroundColor: theme.relode.colors.pageSecondaryBackground,
                padding: theme.spacing(2, 2, 0, 2),
                display: "flex",
                flexDirection: "column",
            },
            label: {
                margin: theme.spacing(1, 0, 2, 0),
            },
            durationDisplay: {
                fontWeight: "lighter",
                fontFamily: "inherit",
            },
            questionsText: {
                margin: theme.spacing(2, 0),
            },
            contactDetailsFlex: {
                display: "flex",
                flexFlow: "row wrap",
                gap: theme.spacing(12),
            },
            footer: {
                margin: `${theme.spacing(4)}px 0`,
                display: "flex",
                alignItems: "center",
                flexDirection: "column",
            },
            logoFooter: {
                marginBottom: theme.spacing(4),
            },
            social: {
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                "& img": {
                    marginLeft: theme.spacing(2),
                    marginRight: theme.spacing(2),
                },
            },
        }),
    { name: "InterviewNotificationEmailPreview" }
);

interface InterviewNotificationEmailPreviewProps {
    candidateFirstName: string;
    candidateManagedBy?: Stakeholder;
    jobName: string;
    companyName: string;
    interviewStartDateTime: string;
    interviewTimeZoneId: number;
    interviewDurationHours: number;
    interviewDurationMinutes: number;
    interviewDetails: string;
    companyBackground: string;
    jobJobManager: Stakeholder;
}

export const InterviewNotificationEmailPreview = (
    props: InterviewNotificationEmailPreviewProps
) => {
    const classes = useStyles({});

    return (
        <Page
            id="interviewNotificationEmailPreview"
            showHeader={false}
            pageType="secondary"
            className={classes.root}
        >
            <PageSection>
                <EmailPreview {...props} />
            </PageSection>
        </Page>
    );
};

const EmailPreview = (props: InterviewNotificationEmailPreviewProps) => {
    const classes = useStyles({});
    const {
        candidateFirstName,
        candidateManagedBy,
        jobName,
        companyName,
        companyBackground,
        jobJobManager,
        interviewStartDateTime,
        interviewTimeZoneId,
        interviewDurationHours,
        interviewDurationMinutes,
        interviewDetails,
    } = props;
    const user = getUserFromStorage();

    const displayDateTime = () => {
        const date = interviewStartDateTime.split(" ")[0];
        const convertedDate = new Date(date);
        const weekday = [
            "Sunday",
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
        ];
        const month = [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
        ];

        return `${weekday[convertedDate.getDay()]},
            ${" "}
            ${month[convertedDate.getMonth()]}
            ${" "}
            ${date.split("/")[1]}
            ${" "}
            ${interviewStartDateTime.split(" ").slice(1).join(" ")}${" "}
            ${TimeZone.fromId(interviewTimeZoneId).abbreviation}`.toLowerCase();
    };

    const displayDuration = () => {
        let durationString = "";
        if (interviewDurationHours > 0) {
            durationString += `${interviewDurationHours}`;
            if (interviewDurationHours === 1) {
                durationString += " hr";
            } else {
                durationString += " hrs";
            }
        }
        if (interviewDurationMinutes > 0) {
            durationString += ` ${interviewDurationMinutes} mins`;
        }
        if (interviewDurationHours <= 0 && interviewDurationMinutes <= 0) {
            durationString = "n/a";
        }
        return `(duration: ${durationString})`;
    };

    return (
        <div className={classes.emailContent}>
            <div className={classes.header}>
                <img src={RelodeLogoHeader} width="127px" height="30px" />
            </div>
            <div className={classes.emailBodyContainer}>
                <Paper className={classes.paper} square>
                    <Text variant="h1">upcoming interview</Text>
                    <Text variant="h6" className={classes.address}>
                        Hello {candidateFirstName},
                    </Text>
                    <Text variant="h6" className={classes.emailBodyText}>
                        We know you haven't forgotten, but thought you would
                        appreciate a quick reminder:
                    </Text>
                    <div className={classes.emailBodyCard}>
                        <Label label="company name" className={classes.label}>
                            <Text variant="h6">{companyName}</Text>
                        </Label>
                        <Label label="job title" className={classes.label}>
                            <Text variant="h6">{jobName}</Text>
                        </Label>
                        <Label label="date and time" className={classes.label}>
                            <div>
                                <Text variant="h3">{displayDateTime()}</Text>
                                <Text
                                    variant="h3"
                                    className={classes.durationDisplay}
                                >
                                    {displayDuration()}
                                </Text>
                            </div>
                        </Label>
                        {interviewDetails?.length > 0 ? (
                            <div className={classes.label}>
                                <DisplayField
                                    label="interview details"
                                    mode="textWithLinks"
                                    value={interviewDetails}
                                />
                            </div>
                        ) : null}
                        {companyBackground?.length > 0 ? (
                            <div className={classes.label}>
                                <DisplayField
                                    label="company information"
                                    mode="textWithLinks"
                                    value={companyBackground}
                                />
                            </div>
                        ) : null}
                    </div>
                    <Text variant="h6" className={classes.questionsText}>
                        For questions or to reschedule, please contact your
                        Relode team below:
                    </Text>
                    {candidateManagedBy ? (
                        <>
                            <div className={classes.emailBodyCard}>
                                <Label
                                    label="recruiter"
                                    className={classes.label}
                                >
                                    <Text variant="h6">
                                        {candidateManagedBy.name}
                                    </Text>
                                </Label>
                                <div className={classes.contactDetailsFlex}>
                                    <Label
                                        label="phone number"
                                        className={classes.label}
                                    >
                                        <Link
                                            href={formatTelUri(
                                                candidateManagedBy.phoneNumber
                                            )}
                                            hideHighlight={false}
                                        >
                                            <Text variant="h6">
                                                {formatPhoneText(
                                                    candidateManagedBy.phoneNumber
                                                )}
                                            </Text>
                                        </Link>
                                    </Label>
                                    <Label
                                        label="email address"
                                        className={classes.label}
                                        key={`managedBy-email-${candidateManagedBy.email}`}
                                    >
                                        <Link
                                            href={`mailto:${candidateManagedBy.email}`}
                                            hideHighlight={false}
                                        >
                                            <Text variant="h6">
                                                {candidateManagedBy.email}
                                            </Text>
                                        </Link>
                                    </Label>
                                </div>
                            </div>
                            <br />
                        </>
                    ) : null}
                    <div className={classes.emailBodyCard}>
                        <Label label="job manager" className={classes.label}>
                            <Text variant="h6">{jobJobManager.name}</Text>
                        </Label>
                        <div className={classes.contactDetailsFlex}>
                            <Label
                                label="phone number"
                                className={classes.label}
                            >
                                <Link
                                    href={formatTelUri(
                                        jobJobManager.phoneNumber
                                    )}
                                    hideHighlight={false}
                                >
                                    <Text variant="h6">
                                        {formatPhoneText(
                                            jobJobManager.phoneNumber
                                        )}
                                    </Text>
                                </Link>
                            </Label>
                            <Label
                                label="email address"
                                className={classes.label}
                            >
                                <Link
                                    href={`mailto:${jobJobManager.email}`}
                                    hideHighlight={false}
                                >
                                    <Text variant="h6">
                                        {jobJobManager.email}
                                    </Text>
                                </Link>
                            </Label>
                        </div>
                    </div>
                    <br />
                    <Text variant="h6">Good luck!</Text>
                    <Text variant="h6">{user.displayName}</Text>
                </Paper>
            </div>
            <div className={classes.footer}>
                <img
                    src={RelodeLogoFooter}
                    width="42px"
                    height="42px"
                    className={classes.logoFooter}
                />
                <Text variant="body2" component="p">
                    ©{new Date().getFullYear()} relode - all rights reserved
                </Text>
                <Text variant="body2" component="p">
                    120 Brentwood Commons Way Ste 530, Brentwood, TN 37027
                </Text>
            </div>
            <div className={classes.social}>
                <img src={FacebookLogo} width="10" height="20" />
                <img src={TwitterLogo} width="20" height="15" />
                <img src={InstagramLogo} width="18" height="18" />
            </div>
        </div>
    );
};
